import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/user/Layout';
import { Save, User, Lock, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { user, updatePassword, updateProfile } = useAuth();
  const location = useLocation();
  
  const [fullName, setFullName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  // Check if user came from password reset
  const isFromReset = location.search.includes('type=recovery');

  useEffect(() => {
    if (user) {
      setFullName(user.user_metadata?.full_name || '');
    }
    
    // If coming from password reset, show password section
    if (isFromReset) {
      setShowPasswordSection(true);
      // Scroll to password section
      setTimeout(() => {
        const passwordSection = document.getElementById('password-section');
        if (passwordSection) {
          passwordSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [user, isFromReset]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t('passwordsDoNotMatch'));
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t('passwordMinLength'));
      return;
    }
    
    try {
      setLoading(true);
      
      const { error } = await updatePassword(newPassword);
      
      if (error) throw error;
      
      toast.success(t('passwordUpdated'));
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);
    } catch (error: any) {
      toast.error(error.message || t('passwordUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!fullName) {
      toast.error(t('fullNameRequired'));
      return;
    }
    
    try {
      setProfileLoading(true);
      
      const { error: profileError } = await updateProfile(fullName);
      
      if (profileError) throw profileError;
      
      toast.success(t('profileUpdated'));
    } catch (error: any) {
      toast.error(error.message || t('profileUpdateFailed'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteAccountRedirect = () => {
    window.open('https://formshare.ai/r/LJXDbiI3ha', '_blank');
  };

  return (
    <Layout title={t('settings')}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
            <User className="h-5 w-5 mr-2 text-blue-600" />
            {t('profileSettings')}
          </h3>
          
          <form onSubmit={handleProfileUpdate}>
            <div className="mb-4">
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                {t('fullName')}
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('email')}
              </label>
              <input
                id="email"
                type="email"
                value={user?.email || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                disabled
              />
              <p className="mt-1 text-xs text-gray-500">{t('emailCannotBeChanged')}</p>
            </div>
            
            <button
              type="submit"
              disabled={profileLoading}
              className="w-full flex justify-center items-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {profileLoading ? t('updating') : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  {t('updateProfile')}
                </>
              )}
            </button>
          </form>
        </div>
        
        <div id="password-section" className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
            <Lock className="h-5 w-5 mr-2 text-blue-600" />
            {t('changePassword')}
          </h3>
          
          {!showPasswordSection && !isFromReset ? (
            <div className="text-center py-8">
              <button
                onClick={() => setShowPasswordSection(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                {t('changePassword')}
              </button>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange}>
              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('newPassword')}
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  autoComplete="new-password"
                />
              </div>
              
              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('confirmNewPassword')}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  autoComplete="new-password"
                />
              </div>
              
              <div className="flex space-x-4">
                {!isFromReset && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    {t('cancel')}
                  </button>
                )}
                
                <button
                  type="submit"
                  disabled={loading}
                  className={`${isFromReset ? 'w-full' : 'flex-1'} flex justify-center items-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50`}
                >
                  {loading ? t('updating') : (
                    <>
                      <Save className="h-5 w-5 mr-2" />
                      {t('updatePassword')}
                    </>
                  )}
                </button>
              </div>
              
              {isFromReset && (
                <p className="mt-4 text-sm text-gray-500 text-center">
                  {t('passwordResetComplete')}
                </p>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Delete Account Section */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
          <Trash2 className="h-5 w-5 mr-2 text-red-600" />
          {t('deleteAccount')}
        </h3>
        
        <div className="border-t border-gray-200 pt-4">
          <p className="text-gray-600 mb-6">
            {t('deleteAccountDescription')}
          </p>
          
          <button
            onClick={handleDeleteAccountRedirect}
            className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
          >
            <Trash2 className="h-5 w-5 mr-2" />
            {t('deleteMyAccount')}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;